import React from "react";

import { Grid, Typography } from "@material-ui/core";

import {
  Create,
  email,
  maxLength,
  minLength,
  regex,
  required,
  SimpleForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useRedirect,
} from "react-admin";

import { useUser } from "../../../authenticatorApp/UserContext";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { AclType, REGEX_PHONE } from "../../../utils/constants";
import {
  carrierCompanyJunctions,
  getCarrierYardOptions,
  parsePhoneDigits,
} from "../../../utils/carrierUserUtils";
import { ValidateCreateButton } from "./ValidateCreateButton";

const validateRequired = required();
const validateEmail = [required(), email()];
// Optional, as on the manager's invite form and on the carrier record itself.
// Everyone invited from here joins as dispatch, and a dispatcher is reached by
// email — only a driver needs a number, for the (phone, lastName) lookup the
// gate finds them by.
const validatePhone = [
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
];

const CarrierUserCreateForm = ({ reloadYardNames, ...props }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <TextInput
        source="firstName"
        label="First Name"
        variant="outlined"
        fullWidth
        validate={validateRequired}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextInput
        source="lastName"
        label="Last Name"
        variant="outlined"
        fullWidth
        validate={validateRequired}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextInput
        source="email"
        type="email"
        variant="outlined"
        fullWidth
        parse={(v) => (v !== "" ? v.toLowerCase() : undefined)}
        validate={validateEmail}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextInput
        source="phone"
        type="tel"
        variant="outlined"
        fullWidth
        parse={parsePhoneDigits}
        validate={validatePhone}
      />
    </Grid>
    {/* Said rather than asked. The invite covers every transload the inviter
        works with, so the only thing left to do about it is know it — and a
        dispatcher who works one transload is told nothing they did not
        already assume. */}
    {reloadYardNames.length > 1 && (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          This user will be able to work with all of your transloads:{" "}
          {reloadYardNames.join(", ")}.
        </Typography>
      </Grid>
    )}
  </Grid>
);

const CarrierUserCreateActions = (props) => (
  <TopToolbar>
    <BackButton {...props} />
  </TopToolbar>
);

const CarrierUserCreateToolbar = (props) => (
  <Toolbar {...props}>
    <ValidateCreateButton />
  </Toolbar>
);

export const CarrierUserCreate = (props) => {
  const { appUser, isAuthorized } = useUser();
  const redirect = useRedirect();

  // Same gate as the Users menu entry and the list's Create button. Typing the
  // route is not a way past it.
  if (appUser && !isAuthorized(AclType.USERS)) redirect("/");

  // Named for the caption only. Which transloads are granted is decided at save
  // time from the inviter's own memberships under the company in session — the
  // same pool, read the same way — so nothing here can drift from what is
  // written, a stale form cannot grant a transload since withdrawn, and the
  // caption cannot name another company's yard the invite will not grant.
  const reloadYardNames = getCarrierYardOptions(
    carrierCompanyJunctions(appUser?.reloadYardCarriers?.items, {
      businessName: appUser?.reloadYardCarrier?.businessName,
      carrierId: appUser?.carrierId,
    }),
    appUser?.appType,
  ).map((yard) => yard.name);

  return (
    <Create
      {...props}
      title={<ForceTitle title="New User" />}
      actions={<CarrierUserCreateActions />}
    >
      <SimpleForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={<CarrierUserCreateToolbar />}
      >
        <CarrierUserCreateForm reloadYardNames={reloadYardNames} />
      </SimpleForm>
    </Create>
  );
};
